import React, { useEffect, useState } from 'react';
import { Box, Grid, Typography, Button, IconButton, CircularProgress, Skeleton, Card, CardContent } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';

import SettingsIcon from '@mui/icons-material/Settings';
import RefreshIcon from '@mui/icons-material/Refresh';
import { DashboardService } from './service';
import { HotelService } from '../../api/services/hotelService';
import { setActiveCurrency } from '../../utils/currency';

// Components
import OccupancySummary from './components/OccupancySummary';
import RevenueSummary from './components/RevenueSummary';
import RoomStatusOverview from './components/RoomStatusOverview';
import StaffOnDuty from './components/StaffOnDuty';
import AlertsPanel from './components/AlertsPanel';
import PerformanceChart from './components/PerformanceChart';
import RevenueBreakdownChart from './components/RevenueBreakdownChart';
import NightAuditWidget from './components/NightAuditWidget';
import ActivityFeed from './components/ActivityFeed';
import CustomiseDashboardModal from './components/CustomiseDashboardModal';
import NightAuditModal from '../Finance/components/NightAuditModal';

const normalizeRole = (role) => String(role || '').replace(/[_\s-]+/g, '').toUpperCase();

const CardSkeleton = ({ height = 240 }) => (
    <Card sx={{ borderRadius: 3, boxShadow: '0 1px 3px rgba(0,0,0,0.04)', border: '1px solid #f1f5f9', height: height }}>
        <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ width: '100%' }}>
                    <Skeleton variant="text" width="60%" height={30} />
                    <Skeleton variant="text" width="40%" height={20} />
                </Box>
                <Skeleton variant="circular" width={40} height={40} />
            </Box>
            <Skeleton variant="rectangular" height={height - 130} sx={{ borderRadius: 2 }} />
        </CardContent>
    </Card>
);

const mapApiDataToDashboard = (apiData) => {
    if (!apiData) return null;
    
    // 1. Occupancy
    const rate = Number(apiData.occupancy?.rate) || 0;
    const trend = apiData.occupancy?.trend || 0;
    const trendDirection = typeof trend === 'number' ? (trend >= 0 ? 'up' : 'down') : (trend || 'up');
    
    const occupancy = {
        rate: rate,
        trend: trendDirection,
        trendValue: Math.abs(Number(trend) || 0),
        occupied: Number(apiData.occupancy?.occupied) || 0,
        total: Number(apiData.occupancy?.total) || 0,
        arrivals: Number(apiData.occupancy?.arrivals) || 0,
        departures: Number(apiData.occupancy?.departures) || 0,
        pendingCheckins: apiData.occupancy?.pendingCheckIns !== undefined 
            ? Number(apiData.occupancy.pendingCheckIns) 
            : (Number(apiData.occupancy?.pendingCheckins) || 0)
    };

    // 2. Revenue
    const revenue = {
        today: Number(apiData.revenue?.today) || 0,
        todayTrend: Number(apiData.revenue?.todayTrend) || 0,
        outstandingDues: Number(apiData.revenue?.outstandingDues) || 0,
        overdueCount: Number(apiData.revenue?.overdueCount) || 0,
        monthToDate: apiData.revenue?.mtd !== undefined 
            ? Number(apiData.revenue.mtd) 
            : (Number(apiData.revenue?.monthToDate) || 0),
        monthTrend: apiData.revenue?.mtdTrend !== undefined 
            ? Number(apiData.revenue.mtdTrend) 
            : (Number(apiData.revenue?.monthTrend) || 0)
    };

    // 3. Room Status
    const roomStatus = {
        clean: apiData.roomStatus?.Clean !== undefined ? Number(apiData.roomStatus.Clean) : (Number(apiData.roomStatus?.clean) || 0),
        cleaning: apiData.roomStatus?.Cleaning !== undefined ? Number(apiData.roomStatus.Cleaning) : (Number(apiData.roomStatus?.cleaning) || 0),
        repair: apiData.roomStatus?.Repair !== undefined ? Number(apiData.roomStatus.Repair) : (Number(apiData.roomStatus?.repair) || 0),
        ooo: apiData.roomStatus?.OOO !== undefined ? Number(apiData.roomStatus.OOO) : (Number(apiData.roomStatus?.ooo) || 0),
        occupied: apiData.roomStatus?.Occupied !== undefined ? Number(apiData.roomStatus.Occupied) : (Number(apiData.roomStatus?.occupied) || 0),
        vacant: apiData.roomStatus?.Available !== undefined ? Number(apiData.roomStatus.Available) : (Number(apiData.roomStatus?.vacant) || 0),
        total: Number(apiData.roomStatus?.total) || 0
    };

    // 4. Reservations / ReservationActivity
    const activitySource = apiData.reservationActivity || apiData.reservations;
    const reservations = {
        newBookings: {
            count: Number(activitySource?.newBookings?.count) || 0,
            trend: Number(activitySource?.newBookings?.trend) || 0
        },
        checkins: {
            count: Number(activitySource?.checkIns !== undefined ? activitySource.checkIns?.count : (activitySource?.checkins?.count || 0)),
            trend: Number(activitySource?.checkIns !== undefined ? activitySource.checkIns?.trend : (activitySource?.checkins?.trend || 0))
        },
        checkouts: {
            count: Number(activitySource?.checkOuts !== undefined ? activitySource.checkOuts?.count : (activitySource?.checkouts?.count || 0)),
            trend: Number(activitySource?.checkOuts !== undefined ? activitySource.checkOuts?.trend : (activitySource?.checkouts?.trend || 0))
        },
        cancellations: {
            count: Number(activitySource?.cancellations?.count) || 0,
            trend: Number(activitySource?.cancellations?.trend) || 0
        }
    };

    // 5. Housekeeping
    const housekeeping = {
        ready: apiData.housekeeping?.Ready !== undefined ? Number(apiData.housekeeping.Ready) : (Number(apiData.housekeeping?.ready) || 0),
        inProgress: apiData.housekeeping?.InProgress !== undefined ? Number(apiData.housekeeping.InProgress) : (Number(apiData.housekeeping?.inProgress) || 0),
        dirty: apiData.housekeeping?.Inspection !== undefined ? (Number(apiData.housekeeping.Inspection) + (Number(apiData.housekeeping.Completed) || 0)) : (Number(apiData.housekeeping?.dirty) || 0),
        ooo: apiData.housekeeping?.OOO !== undefined ? Number(apiData.housekeeping.OOO) : (Number(apiData.housekeeping?.ooo) || 0),
        businessCritical: Number(apiData.housekeeping?.businessCritical) || 0
    };

    // 6. Staff On Duty
    const staffOnDuty = {
        total: Number(apiData.staffOnDuty?.total) || 0,
        required: apiData.staffOnDuty?.totalScheduled !== undefined ? Number(apiData.staffOnDuty.totalScheduled) : (Number(apiData.staffOnDuty?.required) || 0),
        departments: apiData.staffOnDuty?.departments || [],
        checkedIn: apiData.staffOnDuty?.checkedIn,
        checkedOut: apiData.staffOnDuty?.checkedOut,
        shift: apiData.staffOnDuty?.shift
    };

    // 7. Night Audit
    const nightAudit = {
        lastAudit: apiData.nightAudit?.lastAuditAt !== undefined ? apiData.nightAudit.lastAuditAt : (apiData.nightAudit?.lastAudit || null),
        status: apiData.nightAudit?.status || (apiData.nightAuditPending ? 'Pending' : 'Completed'),
        balance: apiData.nightAudit?.balance !== undefined ? apiData.nightAudit.balance : (apiData.revenue?.outstandingDues ?? 0),
        unpostedFolios: apiData.nightAudit?.unpostedFolios !== undefined ? apiData.nightAudit.unpostedFolios : (apiData.revenue?.overdueCount ?? 0),
        cycleDate: apiData.nightAudit?.cycleDate || apiData.businessDate || null
    };

    // 8. Alerts
    const alerts = apiData.alerts || null;

    // 9. Performance
    const performance = apiData.performance || null;

    // 10. Revenue Breakdown
    const revenueBreakdown = apiData.revenueBreakdown || null;

    // 11. Recent Activity
    const recentActivity = apiData.recentActivity || null;

    return {
        occupancy,
        revenue,
        roomStatus,
        reservations,
        housekeeping,
        staffOnDuty,
        nightAudit,
        alerts,
        performance,
        revenueBreakdown,
        recentActivity
    };
};

const WIDGET_METADATA = {
    occupancy: {
        id: 'occupancy',
        xs: 12, sm: 6, lg: 3,
        component: (data) => 
            <OccupancySummary data={data?.occupancy} />,
        permission: (hasPermission) => hasPermission(["CAN_VIEW_ROOMS", "CAN_VIEW_DASHBOARD"])
    },
    revenue: {
        id: 'revenue',
        xs: 12, sm: 6, lg: 3,
        component: (data) => 
            <RevenueSummary data={data?.revenue} />,
        permission: (hasPermission) => hasPermission(["CAN_VIEW_FINANCIALS"])
    },
    roomStatus: {
        id: 'roomStatus',
        xs: 12, sm: 6, lg: 3,
        component: (data) => 
            <RoomStatusOverview data={data?.roomStatus} />,
        permission: (hasPermission) => hasPermission(["CAN_VIEW_ROOMS"])
    },
    staffOnDuty: {
        id: 'staffOnDuty',
        xs: 12, sm: 6, lg: 3,
        component: (data) => 
            <StaffOnDuty data={data?.staffOnDuty} />,
        permission: (hasPermission) => hasPermission(["CAN_MANAGE_STAFF"])
    },
    weeklyChart: {
        id: 'weeklyChart',
        xs: 12, lg: 8,
        component: (data) => 
            <PerformanceChart initialData={data?.performance} />,
        permission: (hasPermission) => hasPermission(["CAN_VIEW_REPORTS"])
    },
    revenueBreakdown: {
        id: 'revenueBreakdown',
        xs: 12, lg: 4,
        component: (data) => 
            <RevenueBreakdownChart initialData={data?.revenueBreakdown} />,
        permission: (hasPermission) => hasPermission(["CAN_VIEW_REPORTS", "CAN_VIEW_FINANCIALS"])
    },
    nightAudit: {
        id: 'nightAudit',
        xs: 12, md: 4,
        component: (data) => 
            <NightAuditWidget data={data?.nightAudit} />,
        permission: (hasPermission) => hasPermission(["CAN_VIEW_NIGHT_AUDIT"])
    },
    alerts: {
        id: 'alerts',
        xs: 12, md: 4,
        component: (data, isSuperAdmin) => 
            <AlertsPanel initialData={data?.alerts} isSuperAdmin={isSuperAdmin} />,
        permission: (hasPermission) => hasPermission(["CAN_VIEW_LOGS", "CAN_VIEW_DASHBOARD"])
    },
    activityLog: {
        id: 'activityLog',
        xs: 12, md: 4,
        component: (data) => 
            <ActivityFeed initialData={data?.recentActivity} />,
        permission: (hasPermission) => hasPermission(["CAN_VIEW_LOGS"])
    }
};

const DEFAULT_ORDER = [
    'occupancy',
    'revenue',
    'roomStatus',
    'staffOnDuty',
    'weeklyChart',
    'revenueBreakdown',
    'nightAudit',
    'alerts',
    'activityLog'
];

const DEFAULT_HIDDEN = [];

export default function Dashboard() {
    const dispatch = useDispatch();
    const { user, tenants } = useSelector((state) => state.auth);
    
    const [dashboardData, setDashboardData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    
    const [widgetOrder, setWidgetOrder] = useState(DEFAULT_ORDER);
    const [hiddenWidgets, setHiddenWidgets] = useState(DEFAULT_HIDDEN);
    const [prefLoading, setPrefLoading] = useState(true);
    const [customiseOpen, setCustomiseOpen] = useState(false);
    const [nightAuditModalOpen, setNightAuditModalOpen] = useState(false);

    const fetchStats = async (isSilent = false) => {
        if (!isSilent) setLoading(true);
        else setRefreshing(true);
        try {
            const authData = JSON.parse(localStorage.getItem('adminAuth') || '{}');
            const tenantId = authData.tenantId || user?.tenantId || '1';
            const activeTenant = (tenants || []).find(t => 
                String(t.tenantId || t.id || t._id) === String(tenantId) ||
                (authData.hotelId && String(t.id || t._id) === String(authData.hotelId))
            );
            const hotelId = activeTenant?.id || activeTenant?._id || authData.hotelId;
            if (hotelId) {
                try {
                    const hotelRes = await HotelService.getById(hotelId, tenantId);
                    const hotel = hotelRes?.data || hotelRes;
                    if (hotel?.currency) {
                        setActiveCurrency(hotel.currency);
                    }
                } catch (_) {}
            }
            const res = await DashboardService.fetchStats(tenantId);
            setDashboardData(mapApiDataToDashboard(res));
        } catch (err) {
            console.error('Failed to fetch dashboard stats:', err);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => {
        const loadPreferences = async () => {
            setPrefLoading(true);
            try {
                const authData = JSON.parse(localStorage.getItem('adminAuth') || '{}');
                const tenantId = authData.tenantId || user?.tenantId || '1';

                let localPrefs = null;
                try {
                    const raw = localStorage.getItem(`dashboard_preferences_${tenantId}`);
                    if (raw) localPrefs = JSON.parse(raw);
                } catch (_) {}

                const prefs = await DashboardService.fetchPreferences(tenantId);
                const validIds = Object.keys(WIDGET_METADATA);

                // Detect legacy/unmigrated preferences
                const isLegacyLayout = !localPrefs ||
                    !localPrefs.v2 ||
                    (Array.isArray(localPrefs.widgetOrder) && localPrefs.widgetOrder[3] !== 'staffOnDuty') ||
                    (Array.isArray(localPrefs.widgetOrder) && localPrefs.widgetOrder.length > 9);

                if (!isLegacyLayout && localPrefs && Array.isArray(localPrefs.widgetOrder)) {
                    const cleanOrder = localPrefs.widgetOrder.filter(id => validIds.includes(id));
                    validIds.forEach(id => {
                        if (!cleanOrder.includes(id)) cleanOrder.push(id);
                    });
                    setWidgetOrder(cleanOrder);
                    if (Array.isArray(localPrefs.hiddenWidgets)) {
                        setHiddenWidgets(localPrefs.hiddenWidgets.filter(id => validIds.includes(id)));
                    }
                } else if (!isLegacyLayout && prefs) {
                    const fetchedOrder = prefs.widgetOrder || prefs.order;
                    const fetchedHidden = prefs.hiddenWidgets || prefs.hidden;
                    if (Array.isArray(fetchedOrder)) {
                        const cleanOrder = fetchedOrder.filter(id => validIds.includes(id));
                        validIds.forEach(id => {
                            if (!cleanOrder.includes(id)) cleanOrder.push(id);
                        });
                        setWidgetOrder(cleanOrder);
                    }
                    if (Array.isArray(fetchedHidden)) {
                        setHiddenWidgets(fetchedHidden.filter(id => validIds.includes(id)));
                    }
                } else {
                    // Initialize with exact reference design layout
                    setWidgetOrder(DEFAULT_ORDER);
                    setHiddenWidgets(DEFAULT_HIDDEN);
                    try {
                        localStorage.setItem(`dashboard_preferences_${tenantId}`, JSON.stringify({
                            v2: true,
                            widgetOrder: DEFAULT_ORDER,
                            hiddenWidgets: DEFAULT_HIDDEN
                        }));
                    } catch (_) {}
                }
            } catch (err) {
                console.error('Failed to fetch widget preferences, falling back to system defaults:', err);
                setWidgetOrder(DEFAULT_ORDER);
                setHiddenWidgets(DEFAULT_HIDDEN);
            } finally {
                setPrefLoading(false);
            }
        };

        loadPreferences();
    }, [user?.tenantId]);

    useEffect(() => {
        dispatch.auth.fetchMeAsync();
        fetchStats();

        // Setup background auto-refresh interval (every 60 seconds)
        const interval = setInterval(() => {
            fetchStats(true);
        }, 60000);

        return () => clearInterval(interval);
    }, [dispatch]);

    const effectivePermissions = React.useMemo(() => {
        if (!user) return [];
        
        let rawPerms = [];
        if (Array.isArray(user.customPermissions) && user.customPermissions.length > 0) {
            rawPerms = user.customPermissions;
        } else if (Array.isArray(user.permissions) && user.permissions.length > 0) {
            rawPerms = user.permissions;
        } else if (user.role && Array.isArray(user.role.permissions)) {
            rawPerms = user.role.permissions;
        }

        return rawPerms.map(p => {
            if (typeof p === 'string') return p;
            if (p && typeof p === 'object') {
                return p.key || p.name || p.code || '';
            }
            return '';
        }).filter(Boolean);
    }, [user]);

    const hasPermission = React.useCallback((perms) => {
        if (effectivePermissions.includes("*")) return true;
        return perms.some(perm => effectivePermissions.includes(perm));
    }, [effectivePermissions]);

    const isSuperAdmin = React.useMemo(() => {
        if (!user) return false;
        const roleName = typeof user.role === 'object' ? user.role.name : user.role;
        return normalizeRole(roleName) === 'SUPERADMIN' || effectivePermissions.includes("*");
    }, [user, effectivePermissions]);

    const isHotelAdmin = React.useMemo(() => {
        if (!user) return false;
        const roleName = typeof user.role === 'object' ? user.role.name : user.role;
        const norm = normalizeRole(roleName);
        return norm === 'HOTELADMIN' || norm === 'ADMIN';
    }, [user]);

    const isAccountant = React.useMemo(() => {
        if (!user) return false;
        const roleName = typeof user.role === 'object' ? user.role.name : user.role;
        const norm = normalizeRole(roleName);
        return norm === 'ACCOUNTANT' || norm === 'AUDITOR';
    }, [user]);

    if (prefLoading) {
        return (
            <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a' }}>
                        Dashboard Overview
                    </Typography>
                </Box>
                <Grid container spacing={2.5}>
                    <Grid item xs={12} sm={6} lg={3}>
                        <CardSkeleton height={150} />
                    </Grid>
                    <Grid item xs={12} sm={6} lg={3}>
                        <CardSkeleton height={150} />
                    </Grid>
                    <Grid item xs={12} sm={6} lg={3}>
                        <CardSkeleton height={150} />
                    </Grid>
                    <Grid item xs={12} sm={6} lg={3}>
                        <CardSkeleton height={150} />
                    </Grid>
                    <Grid item xs={12} lg={8}>
                        <CardSkeleton height={320} />
                    </Grid>
                    <Grid item xs={12} lg={4}>
                        <CardSkeleton height={320} />
                    </Grid>
                </Grid>
            </Box>
        );
    }

    return (
        <Box>
            {/* Dashboard Overview Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
                <Box>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a', fontSize: '1.45rem', lineHeight: 1.2 }}>
                        Dashboard Overview
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.82rem', mt: 0.5 }}>
                        Real-time room occupancy, revenue flow, and front desk operations.
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                    <IconButton 
                        onClick={() => fetchStats()} 
                        disabled={loading || refreshing}
                        size="small"
                        sx={{ 
                            bgcolor: '#ffffff', 
                            border: '1px solid #e2e8f0',
                            borderRadius: '8px',
                            p: 0.85,
                            '&:hover': { bgcolor: '#f8fafc' }
                        }}
                    >
                        {loading || refreshing ? (
                            <CircularProgress size={18} color="primary" />
                        ) : (
                            <RefreshIcon sx={{ fontSize: 18, color: '#64748b' }} />
                        )}
                    </IconButton>
                    {(isSuperAdmin || isHotelAdmin) && (
                        <Button 
                            variant="outlined" 
                            startIcon={<SettingsIcon sx={{ color: 'primary.main', fontSize: '1.1rem !important' }} />} 
                            onClick={() => setCustomiseOpen(true)}
                            sx={{ 
                                borderRadius: '8px',
                                borderColor: '#e2e8f0',
                                color: '#334155',
                                fontSize: '0.82rem',
                                fontWeight: 600,
                                textTransform: 'none',
                                px: 1.75,
                                py: 0.6,
                                bgcolor: '#ffffff',
                                boxShadow: 'none',
                                '&:hover': { 
                                    borderColor: '#cbd5e1', 
                                    bgcolor: '#f8fafc' 
                                }
                            }}
                        >
                            Customize Dashboard
                        </Button>
                    )}
                </Box>
            </Box>

            {/* Widgets Grid */}
            <Grid container spacing={2.5}>
                {widgetOrder.map(widgetId => {
                    // Check if widget is hidden by user preferences
                    if (hiddenWidgets.includes(widgetId)) return null;

                    const metadata = WIDGET_METADATA[widgetId];
                    if (!metadata) return null;

                    // Check permission
                    if (!metadata.permission(hasPermission)) return null;

                    const isKpiCard = ['occupancy', 'revenue', 'roomStatus', 'staffOnDuty'].includes(widgetId);
                    const isChart = ['weeklyChart', 'revenueBreakdown'].includes(widgetId);
                    const height = isKpiCard ? 150 : isChart ? 320 : 380;

                    return (
                        <Grid item xs={metadata.xs} sm={metadata.sm} md={metadata.md} lg={metadata.lg} key={widgetId}>
                            {loading ? (
                                <CardSkeleton height={height} />
                            ) : (
                                metadata.component(dashboardData, isSuperAdmin, isHotelAdmin, isAccountant)
                            )}
                        </Grid>
                    );
                })}
            </Grid>

            {/* Customise Dashboard Modal */}
            <CustomiseDashboardModal
                open={customiseOpen}
                onClose={() => setCustomiseOpen(false)}
                widgetOrder={widgetOrder}
                hiddenWidgets={hiddenWidgets}
                tenantId={user?.tenantId}
                onSave={(newOrder, newHidden) => {
                    setWidgetOrder(newOrder);
                    setHiddenWidgets(newHidden);
                }}
            />

            {/* Night Audit Modal */}
            <NightAuditModal
                open={nightAuditModalOpen}
                onClose={() => setNightAuditModalOpen(false)}
                onSuccess={() => {
                    setNightAuditModalOpen(false);
                    window.dispatchEvent(new Event('nightAuditCompleted'));
                    fetchStats(true);
                }}
            />
        </Box>
    );
}